import React from "react";
import { motion } from "framer-motion";
import { Users, BookOpen, Rocket, Sparkles } from "lucide-react";

export default function Careers() {
    return (
        <div className="bg-gray-50 min-h-screen">
            {/* Header - Matching Services Page style */}
            <section className="relative py-24 px-6 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
                <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-blue-900/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 w-1/2 h-full bg-gradient-to-r from-purple-900/20 to-transparent"></div>

                <div className="max-w-4xl mx-auto text-center relative z-10">
                    <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-blue-400 font-semibold tracking-wider uppercase text-sm"
                    >
                        Careers
                    </motion.span>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-5xl md:text-6xl font-extrabold text-white mt-4 mb-6 tracking-tight"
                    >
                        Join the Future of Discovery
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-xl text-slate-300 max-w-4xl mx-auto font-light leading-relaxed"
                    >
                        We are looking for passionate individuals who want to work at the cutting edge of AI and Biology. Whether you are a computational chemist, a data scientist, or a software engineer, there is a place for you here.
                    </motion.p>
                </div>
            </section>

            <section className="max-w-6xl mx-auto px-6 py-16">
                <div className="flex items-center gap-4 mb-16">
                    <div className="h-px bg-slate-300 flex-1"></div>
                    <h2 className="text-3xl font-bold text-slate-800 uppercase tracking-widest text-center px-4">Opportunities & Culture</h2>
                    <div className="h-px bg-slate-300 flex-1"></div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Internship Program */}
                    <div className="bg-white p-10 rounded-2xl shadow-md border border-gray-200">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="p-4 bg-primary-600 text-white rounded-xl shadow-sm">
                                <Rocket size={28} />
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Internship Program</h2>
                        </div>
                        <p className="text-gray-600 mb-6 leading-relaxed">
                            Our internship program is designed to give you real-world experience. You will work on live projects, instructed by industry experts, and contribute to technologies that matter.
                        </p>
                        <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                            <Sparkles size={18} className="text-primary-600" />
                            Who should apply?
                        </h4>
                        <ul className="space-y-3 text-sm text-gray-700 mb-8">
                            <li className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                                <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
                                Background in Life Sciences, Chemistry, or CS
                            </li>
                            <li className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg border border-purple-100">
                                <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                                Passion for drug discovery
                            </li>
                            <li className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                                <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
                                Eager to learn fast
                            </li>
                        </ul>
                        <a href="mailto:info@smartreceptors.com?subject=Internship%20Application" className="inline-block px-8 py-3.5 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors shadow-sm">
                            Apply for Internship
                        </a>
                    </div>

                    {/* Culture Cards */}
                    <div className="space-y-6">
                        <div className="bg-white p-8 rounded-2xl shadow-md border border-gray-200 hover:border-primary-300 transition-colors">
                            <div className="flex items-start gap-4">
                                <div className="p-3 bg-blue-100 text-primary-600 rounded-xl shrink-0">
                                    <Users size={28} />
                                </div>
                                <div>
                                    <h3 className="font-bold text-gray-900 mb-2 text-xl">Collaborative Culture</h3>
                                    <p className="text-sm text-gray-600 leading-relaxed">
                                        Work in a multidisciplinary team where biologists talk to engineers, and chemists brainstorm with data scientists.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-8 rounded-2xl shadow-md border border-gray-200 hover:border-purple-300 transition-colors">
                            <div className="flex items-start gap-4">
                                <div className="p-3 bg-purple-100 text-purple-600 rounded-xl shrink-0">
                                    <BookOpen size={28} />
                                </div>
                                <div>
                                    <h3 className="font-bold text-gray-900 mb-2 text-xl">Continuous Learning</h3>
                                    <p className="text-sm text-gray-600 leading-relaxed">
                                        Access to the latest research papers, conferences, and internal workshops to keep your skills sharp.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-primary-600 to-purple-600 p-8 rounded-2xl shadow-lg text-white">
                            <h3 className="font-bold mb-3 text-xl">Why SmartReceptors?</h3>
                            <ul className="space-y-2 text-sm">
                                <li className="flex items-center gap-2">✨ Work on cutting-edge AI projects</li>
                                <li className="flex items-center gap-2">🚀 Fast-paced startup environment</li>
                                <li className="flex items-center gap-2">🧬 Impact real-world drug discovery</li>
                                <li className="flex items-center gap-2">💡 Learn from industry experts</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
